<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES, MESSAGE_VARIANTS, SENDER_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const {
  content,
  attachments,
  contentAttributes,
  messageType,
  variant,
  sender,
  additionalAttributes,
} = useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isAutomated = computed(() => {
  return variant.value === MESSAGE_VARIANTS.AUTOMATED;
});

const isBot = computed(() => {
  return variant.value === MESSAGE_VARIANTS.BOT;
});

// agent_name da mensagem = quem FALOU de verdade na orquestracao (o sender e
// sempre a porta da caixa). 'clara_-_comercial' -> 'Clara - Comercial'
const prettyAgentName = raw =>
  String(raw)
    .split('_')
    .map(part => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(' ')
    .replace(/\s+-\s+/g, ' - ')
    .trim();

// remetente e bot? (vale mesmo em variantes de ERRO — mensagem que falhou de
// enviar continua sendo fala de bot e precisa da etiqueta de quem falou)
const senderIsBotType = computed(() =>
  [SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(
    sender?.value?.type
  )
);

const botLabel = computed(() => {
  const agentRaw = additionalAttributes?.value?.agent_name;
  if (
    !isBot.value &&
    !isAutomated.value &&
    !senderIsBotType.value &&
    !agentRaw
  ) {
    return '';
  }

  const entryName = sender?.value?.name;
  if (senderIsBotType.value || (entryName && agentRaw)) {
    const base = entryName || 'Brain';
    if (agentRaw) {
      const speaker = prettyAgentName(agentRaw);
      const sameAgent =
        speaker.toLowerCase().replace(/[^a-z0-9]/g, '') ===
        base.toLowerCase().replace(/[^a-z0-9]/g, '');
      // cadeia visivel pro atendente: porta › quem falou
      if (!sameAgent) return `BOT — ${base} › ${speaker}`;
    }
    return `BOT — ${base}`;
  }
  // sem sender mas com agent_name: mostra quem falou
  if (agentRaw) return `BOT — ${prettyAgentName(agentRaw)}`;
  // Sem sender → automação do sistema
  return 'Automação';
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="px-[13px] py-[9px]" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <span
        v-if="botLabel"
        class="text-[10px] uppercase tracking-wider font-medium opacity-70"
        :class="
          isAutomated ? 'text-n-s2-msg-auto-text' : 'text-n-s2-msg-bot-text'
        "
      >
        {{ botLabel }}
      </span>
      <!-- eslint-disable vue/no-bare-strings-in-template -->
      <span
        v-else-if="isTemplate"
        class="text-[10px] uppercase tracking-wider opacity-60 font-medium"
      >
        Template WhatsApp
      </span>
      <!-- eslint-enable vue/no-bare-strings-in-template -->
      <FormattedContent v-if="renderContent" :content="renderContent" />
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate || isAutomated">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
